// components/TextInput.tsx
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';

type Props = {
  fieldType?: 'number' | 'text' | 'email' | 'password';
  startIcon?: string;
  endIcon?: string;
  size?: 'sm' | 'default' | 'lg' | null | undefined;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  maxLength?: number;
  /**
   * HTML autocomplete token threaded to the underlying `Input` so password
   * managers can autofill (e.g. `email`, `current-password`, `new-password`).
   */
  autoComplete?: string;
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & GeneralFormProps;

function RHFShadcnTextField({
  name,
  title,
  description,
  helperText,
  placeholder,
  value: externalValue,
  onChange: externalOnChange,
  required,
  fieldType = 'text',
  startIcon,
  endIcon,
  disabled,
  size = 'default',
  onPaste,
  maxLength,
  autoComplete,
}: Props) {
  const resolvedHelperText = helperText ?? description;
  const translate = useTranslations();
  const { control } = useFormContext<Record<string, string | number>>();

  const hasTitle = title !== undefined && title !== '';
  function buildFloatingLabel(): string | undefined {
    if (hasTitle === false) {
      return undefined;
    }
    if (required === true) {
      return `${title} *`;
    }
    return title;
  }
  const floatingLabel = buildFloatingLabel();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const rawValue = field.value ?? externalValue ?? '';
        // A cleared number field stores null (see onChange), which the `??`
        // above already renders as ''. Also guard against a stray NaN ever
        // reaching here so the input renders empty, not "NaN".
        const displayValue
          = fieldType === 'number'
            && typeof rawValue === 'number'
            && Number.isNaN(rawValue) === true
            ? ''
            : rawValue;
        const stringValue = String(rawValue);
        const currentLength = stringValue.length;
        const showCounter
          = maxLength !== undefined && currentLength >= Math.floor(maxLength * 0.8);

        return (
          <FormItem className="w-full">
            <FormControl>
              <Input
                {...field}
                inputSize={size}
                variant={fieldState.error !== undefined ? 'error' : 'default'}
                disabled={disabled}
                startIcon={startIcon}
                endIcon={endIcon}
                required={required}
                data-testid={field.name}
                type={fieldType}
                label={floatingLabel}
                placeholder={placeholder}
                maxLength={maxLength}
                autoComplete={autoComplete}
                onChange={(e) => {
                  const raw = e.target.value;
                  // A blank/NaN number commits null (not '') so a nullable
                  // number schema (z.number().nullable()) accepts the cleared
                  // value and a non-nullable one correctly fails its required
                  // check; '' would be rejected as "expected number".
                  let value: string | number | null = raw;
                  if (fieldType === 'number') {
                    if (raw === '') {
                      value = null;
                    } else {
                      const parsed = Number(raw);
                      value = Number.isNaN(parsed) === true ? null : parsed;
                    }
                  }

                  const newEvent = { ...e, target: { ...e.target, value } };

                  if (externalOnChange) {
                    externalOnChange(newEvent);
                    return;
                  }
                  field.onChange(newEvent);
                }}
                onPaste={onPaste}
                value={displayValue}
              />
            </FormControl>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {resolvedHelperText !== undefined && resolvedHelperText !== '' && fieldState.error === undefined && (
                  <FormDescription className="mt-1 type-body-s text-on-surface-variant">
                    {resolvedHelperText}
                  </FormDescription>
                )}
                <FormMessage className="mt-1.5" />
              </div>
              {showCounter === true && maxLength !== undefined && (
                <span
                  className="type-body-s tabular-nums text-on-surface-variant shrink-0"
                  aria-hidden="true"
                >
                  {translate('form.charactersUsed', {
                    count: currentLength,
                    max: maxLength,
                  })}
                </span>
              )}
            </div>
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnTextField;
